import {
  createRouter,
  createRoute,
  createRootRoute,
  Outlet,
} from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { Dashboard } from "@/pages/Dashboard";
import { Workspaces } from "@/pages/Workspaces";
import { Catalog } from "@/pages/Catalog";
import { Pipelines } from "@/pages/Pipelines";
import { MetadataProfiles } from "@/pages/MetadataProfiles";
import { Agents } from "@/pages/Agents";
import { Ontology } from "@/pages/Ontology";
import { TermSets } from "@/pages/TermSets";
import { OntologyNew } from "@/pages/OntologyNew";
import { Retrieval } from "@/pages/Retrieval";
import { Evals } from "@/pages/Evals";
import { Packs } from "@/pages/Packs";
import { Governance } from "@/pages/Governance";
import { KnowledgeHealth } from "@/pages/KnowledgeHealth";
import { DecisionDossier } from "@/pages/DecisionDossier";
import { Users } from "@/pages/Users";
import { WorkspaceSettings } from "@/pages/WorkspaceSettings";
import { HowTo } from "@/pages/HowTo";
import { Docs } from "@/pages/Docs";
import { Qna } from "@/pages/Qna";
import { OrbHowTo } from "@/pages/OrbHowTo";
import { UseCases } from "@/pages/UseCases";
import { Login } from "@/pages/Login";
import { Signup } from "@/pages/Signup";
import { VerifyEmail } from "@/pages/VerifyEmail";
import { ResetPassword } from "@/pages/ResetPassword";
import { Welcome } from "@/pages/Welcome";
import { Marketplace } from "@/pages/Marketplace";
import { OnboardingDoc } from "@/pages/OnboardingDoc";
import { Studio } from "@/pages/Studio";
import { Composer } from "@/pages/Composer";
import { Actions } from "@/pages/Actions";
import { Events } from "@/pages/Events";
import { Journey } from "@/pages/Journey";
import { Brief } from "@/pages/Brief";
import { InstructionPlan } from "@/pages/InstructionPlan";
import { AdminTemplates } from "@/pages/AdminTemplates";
import { AdminComposition } from "@/pages/AdminComposition";
import { AdminScaffold } from "@/pages/AdminScaffold";
import { DeveloperPortal } from "@/pages/DeveloperPortal";
import { PrivacyPolicy, TermsAndConditions } from "@/pages/Legal";

const rootRoute = createRootRoute({
  component: () => (
    <Layout>
      <Outlet />
    </Layout>
  ),
});

const routes = [
  createRoute({ getParentRoute: () => rootRoute, path: "/", component: Welcome }),
  createRoute({ getParentRoute: () => rootRoute, path: "/dashboard", component: Dashboard }),
  createRoute({ getParentRoute: () => rootRoute, path: "/workspaces", component: Workspaces }),
  createRoute({ getParentRoute: () => rootRoute, path: "/catalog", component: Catalog }),
  createRoute({ getParentRoute: () => rootRoute, path: "/pipelines", component: Pipelines }),
  createRoute({ getParentRoute: () => rootRoute, path: "/metadata-profiles", component: MetadataProfiles }),
  createRoute({ getParentRoute: () => rootRoute, path: "/agents", component: Agents }),
  createRoute({ getParentRoute: () => rootRoute, path: "/studio", component: Studio }),
  createRoute({ getParentRoute: () => rootRoute, path: "/studio/composer", component: Composer }),
  createRoute({ getParentRoute: () => rootRoute, path: "/actions", component: Actions }),
  createRoute({ getParentRoute: () => rootRoute, path: "/events", component: Events }),
  createRoute({ getParentRoute: () => rootRoute, path: "/ontology", component: Ontology }),
  createRoute({ getParentRoute: () => rootRoute, path: "/ontology/term-sets", component: TermSets }),
  createRoute({ getParentRoute: () => rootRoute, path: "/developer", component: DeveloperPortal }),
  createRoute({ getParentRoute: () => rootRoute, path: "/ontology/new", component: OntologyNew }),
  createRoute({ getParentRoute: () => rootRoute, path: "/retrieval", component: Retrieval }),
  createRoute({ getParentRoute: () => rootRoute, path: "/evals", component: Evals }),
  createRoute({ getParentRoute: () => rootRoute, path: "/packs", component: Packs }),
  createRoute({ getParentRoute: () => rootRoute, path: "/governance", component: Governance }),
  createRoute({ getParentRoute: () => rootRoute, path: "/admin/templates", component: AdminTemplates }),
  createRoute({ getParentRoute: () => rootRoute, path: "/admin/composition-runs", component: AdminComposition }),
  createRoute({ getParentRoute: () => rootRoute, path: "/admin/scaffold", component: AdminScaffold }),
  createRoute({ getParentRoute: () => rootRoute, path: "/knowledge-health", component: KnowledgeHealth }),
  createRoute({ getParentRoute: () => rootRoute, path: "/dossier", component: DecisionDossier }),
  createRoute({ getParentRoute: () => rootRoute, path: "/users", component: Users }),
  createRoute({ getParentRoute: () => rootRoute, path: "/settings", component: WorkspaceSettings }),
  createRoute({ getParentRoute: () => rootRoute, path: "/howto", component: HowTo }),
  createRoute({ getParentRoute: () => rootRoute, path: "/howto/orb", component: OrbHowTo }),
  createRoute({ getParentRoute: () => rootRoute, path: "/docs", component: Docs }),
  createRoute({ getParentRoute: () => rootRoute, path: "/qna", component: Qna }),
  createRoute({ getParentRoute: () => rootRoute, path: "/use-cases", component: UseCases }),
  createRoute({ getParentRoute: () => rootRoute, path: "/journey", component: Journey }),
  createRoute({ getParentRoute: () => rootRoute, path: "/brief", component: Brief }),
  createRoute({ getParentRoute: () => rootRoute, path: "/plan", component: InstructionPlan }),
  createRoute({ getParentRoute: () => rootRoute, path: "/login", component: Login }),
  createRoute({ getParentRoute: () => rootRoute, path: "/signup", component: Signup }),
  createRoute({ getParentRoute: () => rootRoute, path: "/verify-email", component: VerifyEmail }),
  createRoute({ getParentRoute: () => rootRoute, path: "/reset-password", component: ResetPassword }),
  createRoute({ getParentRoute: () => rootRoute, path: "/welcome", component: Welcome }),
  createRoute({ getParentRoute: () => rootRoute, path: "/legal/privacy-policy", component: PrivacyPolicy }),
  createRoute({ getParentRoute: () => rootRoute, path: "/legal/terms-and-conditions", component: TermsAndConditions }),
  createRoute({ getParentRoute: () => rootRoute, path: "/marketplace", component: Marketplace }),
  createRoute({ getParentRoute: () => rootRoute, path: "/onboarding/$slug", component: OnboardingDoc }),
];

const routeTree = rootRoute.addChildren(routes);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
